import { normalizeLanguageId } from "./scope-catalog.js";
import { isPlaceholderTitle, questionTitle } from "./conversation-groups.js";
import { getSafeExternalUrl, getSafeSourceUrl } from "./source-url.js";
import { DEFAULT_MAX_MESSAGE_LENGTH } from "./config.js";
import {
  createSessionHistoryStore,
  getWorkspaceScope,
  scopeMatches,
  scopeMatchesIgnoringLanguage,
  type SessionHistoryStore,
} from "./session-history.js";
import {
  createBlockTypewriter,
  type StreamBufferCallbacks,
  type StreamBuffer,
  type StreamBufferFactory,
} from "./stream-buffer.js";
import type {
  Block,
  ChatConfig,
  ChatAttachmentInput,
  ChatFilterValue,
  ChatHistoryItem,
  ChatHistoryResult,
  ChatHistoryStateEntry,
  ChatMessage,
  ChatPageContext,
  ChatResumeMessage,
  ChatResumeResponse,
  ChatShareResponse,
  ChatState,
  FallbackState,
  FollowUp,
  InitialWorkspaceState,
  PreparedQuestionState,
  SourceReference,
} from "./types.js";
import type { JonasSearchResultDto, StreamHandlers } from "./api-client.js";
import {
  ChatHistoryRemovalError,
  ChatResumeError,
  ChatStreamError,
} from "./api-client.js";
import {
  getLiveFallbackMessage,
  mapJonasResultsToSources,
  stripMarkdownForSearch,
} from "./fallback.js";

export type ChatApi = {
  start(
    payload: unknown,
    handlers: StreamHandlers,
    signal: AbortSignal,
  ): Promise<void>;
  continue(
    payload: unknown,
    handlers: StreamHandlers,
    signal: AbortSignal,
  ): Promise<void>;
  redeem?(shareToken: string): Promise<ChatResumeResponse>;
  cancel?(sessionToken: string): Promise<void>;
  resume?(sessionToken: string): Promise<ChatResumeResponse>;
  getHistory?(scope: {
    product: string;
    languageId: number;
    activation: string;
    activeSessionToken?: string;
  }): Promise<ChatHistoryResult | ChatHistoryItem[]>;
  setHistoryPreference?(keepHistory: boolean): Promise<boolean>;
  startNewOffChat?(): Promise<void>;
  resetHistory?(): Promise<void>;
  removeHistoryItem?(sessionToken: string): Promise<void>;
  postFeedback(payload: unknown): Promise<void>;
  clearFeedback?(payload: unknown): Promise<void>;
  share?(payload: {
    sessionToken: string;
    messageId: string;
  }): Promise<ChatShareResponse>;
  getArticleSource(url: string): Promise<string>;
  search?(params: {
    query: string;
    product: string;
    languageId: number;
    area: string;
    numberOfSources: number;
    signal?: AbortSignal;
  }): Promise<JonasSearchResultDto[]>;
};

export type ChatBootstrapOptions = {
  question?: string;
  entryPoint?: string;
  sourceUrl?: string;
  sourceTitle?: string;
  context?: Partial<ChatPageContext> | Record<string, unknown>;
  historySessionToken?: string;
  prerenderedSourceHtml?: string;
};

export type WorkspaceScopePatch = Partial<
  Pick<ChatPageContext, "product" | "languageId" | "culture" | "activation">
>;

export type RemoveHistoryEntryResult =
  | "removed"
  | "not-found"
  | "rate-limited"
  | "retryable-error"
  | "error";

export type ChatSessionStore = {
  bootstrap(options?: ChatBootstrapOptions): Promise<void>;
  getState(): ChatState;
  subscribe(listener: (state: ChatState) => void): () => void;
  submitQuestion(
    question: string,
    attachments?: ChatAttachmentInput[],
  ): Promise<void>;
  refineFallbackSearch(query: string): Promise<void>;
  reconnectAi(): Promise<void>;
  prepareQuestion(question: string, options?: ChatBootstrapOptions): boolean;
  clearPreparedQuestion(): void;
  startPreparedQuestion(): Promise<void>;
  updateContext(context: Partial<ChatPageContext>): void;
  changeWorkspaceScope(
    patch: WorkspaceScopePatch,
    options?: { preserveConversation?: boolean; navigating?: boolean },
  ): boolean;
  resumeFromHistory(index: number): Promise<boolean>;
  clearHistory(): Promise<void>;
  removeHistoryEntry(sessionToken: string): Promise<RemoveHistoryEntryResult>;
  refreshHistory(): Promise<void>;
  setHistoryPreference(keepHistory: boolean): Promise<void>;
  rotateOffIdentity(): Promise<void>;
  submitFeedback(
    messageId: string,
    value: "positive" | "negative" | null,
  ): Promise<void>;
  createShare(messageId: string): Promise<ChatShareResponse>;
  redeemShare(shareToken: string): Promise<boolean>;
  openSource(url: string, title: string): Promise<void>;
  closeSource(): void;
  stop(): void;
  resetSession(): void;
};

export type ChatSessionStoreDependencies = {
  createBlockTypewriter?: StreamBufferFactory;
  sessionHistory?: SessionHistoryStore | null;
  baseUrl?: string;
  resumePollDelaysMs?: number[];
  resumePendingMaxWaitMs?: number;
  wait?: (ms: number) => Promise<void>;
  // Minimum time the reconnect-in-flight state is held (see RECONNECT_MIN_BUSY_MS).
  // Injectable so tests can disable the busy-state padding and run instantly.
  reconnectMinBusyMs?: number;
  // Minimum spacing between AUTOMATIC reconnect attempts (see
  // SILENT_RECONNECT_MIN_INTERVAL_MS). Injectable so a test can reach the code an
  // expired throttle would reach in production without waiting out the interval.
  reconnectThrottleMs?: number;
  // Tab visibility, injectable because the store runs in tests without a document.
  // Returning to the tab is what re-probes a platform that was down.
  isDocumentHidden?: () => boolean;
  onVisibilityChange?: (handler: () => void) => () => void;
  // Last-known "keep history" preference, persisted client-side so the toggle shows
  // the correct state on first paint. The visitor cookie is Path=/api/chat and is not
  // sent on the page request, so the server can't seed keepHistory at render time; the
  // store reconciles this with the authoritative value on the first successful fetch.
  // Injectable so tests run without a real localStorage (see the localStorage defaults).
  readKeepHistoryPreference?: () => boolean | null;
  writeKeepHistoryPreference?: (keepHistory: boolean) => void;
};

const MAX_ATTACHMENT_COUNT = 4;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const MAX_ATTACHMENT_BASE64_LENGTH = Math.ceil(MAX_ATTACHMENT_BYTES / 3) * 4;

// Delays (ms) before each resume poll after a reload that interrupted an answer.
// The first poll is immediate (0) to catch an answer that completed just before
// the refresh; the backoff then spreads the remaining attempts across ~30s before
// giving up. Injectable via dependencies so tests can run the loop deterministically.
const DEFAULT_RESUME_POLL_DELAYS_MS = [0, 1500, 2500, 4000, 6000, 8000, 8000];

// The platform permits a new turn after a reply has been pending for ten minutes,
// even if a lost generation never emitted a terminal status. Mirror that stale-reply
// safeguard so a refresh cannot leave this page polling indefinitely.
const DEFAULT_RESUME_PENDING_MAX_WAIT_MS = 10 * 60 * 1000;

// When to re-read the history after an answer finished, waiting for the platform's
// summarization prompt to replace the question with a real title.
const SUMMARY_TITLE_REFRESH_DELAYS_MS = [3000, 9000];

// A fallback search also silently re-probes the AI Platform with the ORIGINAL
// question (see refineFallbackSearch). Rapid searches must not hammer the platform,
// so a fresh probe starts at most once per this interval (a cheap client throttle).
const SILENT_RECONNECT_MIN_INTERVAL_MS = 5000;

// A reconnect attempt (the explicit button, the idle poll, or a search probe) holds
// the banner's disabled "Bezig verbinding te herstellen" busy state for at least this
// long. A probe that fails fast then reads as a deliberate transition instead of a
// flicker, and a rapid second trigger stays blocked a little longer (the user asked
// for the button not to be re-usable in quick succession). A successful reconnect has
// already streamed its answer by the time this applies, so it only pads the
// invisible/failed case. Injectable via dependencies so tests run instantly.
const RECONNECT_MIN_BUSY_MS = 1500;

// Durable per-browser key for the last-known "keep history" preference. Kept in
// localStorage (not the per-tab sessionStorage history snapshot) so the toggle shows
// the right state immediately on every tab, before the first /api/chat/history fetch.
const KEEP_HISTORY_STORAGE_KEY = "help-jonas:keep-history";

function defaultReadKeepHistoryPreference(): boolean | null {
  try {
    const raw = globalThis.localStorage?.getItem(KEEP_HISTORY_STORAGE_KEY);
    if (raw === "1") return true;
    if (raw === "0") return false;
    return null;
  } catch {
    // localStorage can throw in privacy modes; fall back to "unknown".
    return null;
  }
}

function defaultWriteKeepHistoryPreference(keepHistory: boolean): void {
  try {
    globalThis.localStorage?.setItem(
      KEEP_HISTORY_STORAGE_KEY,
      keepHistory ? "1" : "0",
    );
  } catch {
    // Best-effort persistence; a failed write just means the next load falls back.
  }
}

export function createChatSessionStore(
  config: ChatConfig,
  initial: InitialWorkspaceState,
  api: ChatApi,
  dependencies: ChatSessionStoreDependencies = {},
): ChatSessionStore {
  const createVisualStreamBuffer =
    dependencies.createBlockTypewriter ?? createBlockTypewriter;
  const baseUrl = dependencies.baseUrl ?? getCurrentBaseUrl();
  const resumePollDelaysMs =
    dependencies.resumePollDelaysMs ?? DEFAULT_RESUME_POLL_DELAYS_MS;
  const resumePendingMaxWaitMs =
    dependencies.resumePendingMaxWaitMs ??
    DEFAULT_RESUME_PENDING_MAX_WAIT_MS;
  const waitFor =
    dependencies.wait ??
    ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const reconnectMinBusyMs =
    dependencies.reconnectMinBusyMs ?? RECONNECT_MIN_BUSY_MS;
  const reconnectThrottleMs =
    dependencies.reconnectThrottleMs ?? SILENT_RECONNECT_MIN_INTERVAL_MS;
  const isDocumentHidden =
    dependencies.isDocumentHidden ??
    ((): boolean => typeof document !== "undefined" && document.hidden === true);
  const onVisibilityChange =
    dependencies.onVisibilityChange ??
    ((handler: () => void): (() => void) => {
      if (typeof document === "undefined") return () => {};
      document.addEventListener("visibilitychange", handler);
      return () => document.removeEventListener("visibilitychange", handler);
    });
  const historyStore =
    dependencies.sessionHistory === undefined
      ? createDefaultSessionHistoryStore()
      : dependencies.sessionHistory;
  const readKeepHistoryPreference =
    dependencies.readKeepHistoryPreference ?? defaultReadKeepHistoryPreference;
  const writeKeepHistoryPreference =
    dependencies.writeKeepHistoryPreference ?? defaultWriteKeepHistoryPreference;
  // Seed the toggle from the last-known client-side preference (default ON: a
  // brand-new visitor who has never used Help gets history enabled, matching the
  // server's default). The first successful history fetch reconciles this with the server.
  const initialKeepHistory = readKeepHistoryPreference() ?? true;
  const initialContext = normalizeWorkspaceContext(initial, baseUrl);
  let activeController: AbortController | null = null;
  let activeStreamBuffer: StreamBuffer | null = null;
  let sourceRequestSequence = 0;
  let historyRequestSequence = 0;
  // Bumped whenever a user action supersedes an in-flight resume poll (a new
  // question, stop, scope change, history switch) so the poll bails without
  // clobbering the newer state.
  let resumePollSequence = 0;
  // Set by a restore whose conversation the platform reports as still generating. The
  // chat projection can lag the turn that was just asked, so the restored thread can end
  // in a completed answer while a newer one is on its way; the poll then still runs.
  let restoredWithPendingAnswer = false;
  // A deferred background AI-reconnect probe (see attemptReconnect): silentProbeController
  // lets a scope reset abort an in-flight probe; the timestamp feeds the
  // SILENT_RECONNECT_MIN_INTERVAL_MS throttle that rate-limits the automatic triggers.
  // The single in-flight guard is state.reconnecting (one reconnect at a time, any trigger).
  let silentProbeController: AbortController | null = null;
  let lastReconnectAt = 0;
  // The idle re-probe chain (see startSilentReconnectPolling): runs only while a live
  // fallback element exists, backs off between attempts, stops when the budget is spent
  // and is cleared on recovery or scope reset. The pending-visibility unsubscribe parks
  // the chain while the tab is hidden.
  let visibilityRecoveryOff: (() => void) | null = null;
  // Fallback-search ordering guard (see refineFallbackSearch): a dedicated controller
  // aborts the previous in-flight search and the sequence number drops any slower
  // older response so it can never overwrite the results of a newer query.
  let fallbackSearchController: AbortController | null = null;
  let fallbackSearchSequence = 0;
  let state: ChatState = {
    status: "idle",
    sessionToken: "",
    messages: [],
    activeSource: null,
    scope: config.scope,
    labels: config.labels,
    maxMessageLength: config.maxMessageLength ?? DEFAULT_MAX_MESSAGE_LENGTH,
    context: initialContext,
    preparedQuestion: null,
    history: [],
    keepHistory: initialKeepHistory,
    feedbackPendingId: "",
    historyStatus: "loading",
    isRestoring: false,
    reconnecting: false,
    error: "",
    retryQuestion: "",
  };
  const listeners = new Set<(state: ChatState) => void>();

  function emit(): void {
    const snapshot = getState();
    for (const listener of listeners) listener(snapshot);
  }

  function getState(): ChatState {
    return {
      ...state,
      context: copyContext(state.context),
      labels: { ...state.labels },
      preparedQuestion: state.preparedQuestion
        ? copyPreparedQuestion(state.preparedQuestion)
        : null,
      history: state.history.map((entry) => ({ ...entry })),
      messages: state.messages.map((message) => ({
        ...message,
        sources: message.sources.map((source) => ({ ...source })),
        followUps: message.followUps.map((followUp) => ({ ...followUp })),
      })),
      activeSource: state.activeSource ? { ...state.activeSource } : null,
    };
  }

  function updateMessage(id: string, update: Partial<ChatMessage>): void {
    state = {
      ...state,
      messages: state.messages.map((message) =>
        message.id === id ? { ...message, ...update } : message,
      ),
    };
    emit();
  }

  // Flips the shared reconnect-in-flight flag (see attemptReconnect) and notifies the
  // view so the availability banner can show / clear its disabled busy state.
  function setReconnecting(value: boolean): void {
    if (state.reconnecting === value) return;
    state = { ...state, reconnecting: value };
    emit();
  }

  async function openSource(url: string, title: string): Promise<void> {
    const safeUrl = getSafeSourceUrl(url, baseUrl);
    if (!safeUrl) return;
    const requestSequence = ++sourceRequestSequence;
    const sourceTitle = title || safeUrl;
    const nextContext = mergeContext({
      selectedSourceUrl: safeUrl,
      sourceTitle,
    });

    state = {
      ...state,
      context: nextContext,
      preparedQuestion: updatePreparedContext(nextContext),
      activeSource: {
        url: safeUrl,
        title: sourceTitle,
        html: "",
        loading: true,
        error: "",
      },
    };
    emit();
    persistCurrentSession();

    try {
      const html = await api.getArticleSource(safeUrl);
      if (!isCurrentSourceRequest(requestSequence, safeUrl)) return;
      state = {
        ...state,
        activeSource: {
          url: safeUrl,
          title: sourceTitle,
          html,
          loading: false,
          error: "",
        },
      };
    } catch (error) {
      if (!isCurrentSourceRequest(requestSequence, safeUrl)) return;
      state = {
        ...state,
        activeSource: {
          url: safeUrl,
          title: sourceTitle,
          html: "",
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : "Bron kan niet worden geopend.",
        },
      };
    }
    emit();
    persistCurrentSession();
  }

  function adoptPrerenderedSource(
    url: string,
    title: string,
    html: string,
  ): void {
    const safeUrl = getSafeSourceUrl(url, baseUrl);
    if (!safeUrl) return;
    ++sourceRequestSequence;
    const sourceTitle = title || safeUrl;
    const nextContext = mergeContext({
      selectedSourceUrl: safeUrl,
      sourceTitle,
    });
    state = {
      ...state,
      context: nextContext,
      preparedQuestion: updatePreparedContext(nextContext),
      activeSource: {
        url: safeUrl,
        title: sourceTitle,
        html,
        loading: false,
        error: "",
      },
    };
    emit();
    persistCurrentSession();
  }

  function isCurrentSourceRequest(sequence: number, url: string): boolean {
    return (
      sourceRequestSequence === sequence && state.activeSource?.url === url
    );
  }

  async function ask(
    question: string,
    attachments: ChatAttachmentInput[] = [],
    options: {
      suppressUserBubble?: boolean;
      reconnect?: boolean;
      silent?: boolean;
      deferMaterialize?: boolean;
    } = {},
  ): Promise<void> {
    const trimmed = question.trim();
    if (
      !trimmed ||
      state.status === "starting" ||
      state.status === "streaming"
    ) {
      return;
    }

    const controller = new AbortController();
    activeController = controller;
    // A silent probe (an automatic AI reconnect, see attemptReconnect) makes NO
    // visible change until the platform actually answers: it defers appending its
    // assistant placeholder and switching to the "starting" status until the first
    // streaming event "materializes" it, AND it swallows failures so a still-down
    // platform leaves the thread exactly as it was (fallback mode, banner, locked
    // composer) with no loader flicker and no error alert. An explicit reconnect uses
    // deferMaterialize instead: it shares the deferred-reveal (so the green "weer
    // beschikbaar" banner only appears once the platform truly responds) but still
    // surfaces a transient failure as the usual bottom alert.
    const silent = options.silent === true;
    const deferred = silent || options.deferMaterialize === true;
    if (silent) silentProbeController = controller;
    let materialized = !deferred;
    const normalizedAttachments = normalizeAttachments(attachments);
    const userMessage: ChatMessage = createMessage(
      "user",
      trimmed,
      false,
      normalizedAttachments,
    );
    const assistantMessage: ChatMessage = createMessage("assistant", "", true);
    let assistantMessageId = assistantMessage.id;
    let streamText = "";
    // Guards the AI-unavailable Jonas-search fallback so the two mutually-exclusive
    // error paths (SSE `error` event, network `catch`) can each call it but it runs once.
    let fallbackTriggered = false;
    let completionPayload: {
      messageId?: string;
      text?: string;
      html?: string;
      blocks?: unknown[];
      followUps?: unknown[];
      sources?: unknown[];
      canGiveFeedback?: boolean;
    } | null = null;
    const messagesToAppend = options.suppressUserBubble
      ? [assistantMessage]
      : [userMessage, assistantMessage];
    // Reveal a deferred silent probe the moment the platform proves it is back:
    // append the assistant placeholder and switch to "starting" so streaming and
    // completion render exactly like a normal answer from here on.
    function materialize(): void {
      if (materialized) return;
      materialized = true;
      state = {
        ...state,
        status: "starting",
        error: "",
        retryQuestion: "",
        messages: [...state.messages, ...messagesToAppend],
      };
      emit();
    }
    if (!deferred) {
      state = {
        ...state,
        status: "starting",
        error: "",
        retryQuestion: "",
        preparedQuestion: null,
        messages: [...state.messages, ...messagesToAppend],
      };
      emit();
      // Persist the question as soon as it is committed. A continue never emits a
      // `session` event, so without this a forced refresh mid-follow-up would lose
      // the just-typed question (the snapshot would still hold the previous answer).
      // For a fresh start this is a no-op until the session token arrives.
      persistCurrentSession();
    }

    function updateAssistantMessage(update: Partial<ChatMessage>): void {
      updateMessage(assistantMessageId, update);
    }

    function setAssistantMessageId(messageId: string | undefined): void {
      if (!messageId || messageId === assistantMessageId) return;
      const currentId = assistantMessageId;
      assistantMessageId = messageId;
      updateMessage(currentId, { id: messageId });
    }

    const streamBuffer = createVisualStreamBuffer({
      onUpdate(blocks: Block[]): void {
        if (activeStreamBuffer !== streamBuffer || controller.signal.aborted) {
          return;
        }
        updateAssistantMessage({ blocks, pending: true });
      },
      onComplete(blocks: Block[]): void {
        if (activeStreamBuffer !== streamBuffer || controller.signal.aborted) {
          return;
        }
        applyCompletedAssistantMessage(blocks);
      },
      onSettled(blocks: Block[]): void {
        if (activeStreamBuffer !== streamBuffer || controller.signal.aborted) {
          return;
        }
        // The visible answer is fully revealed but the follow-ups are still streaming:
        // keep the message pending while flagging it finalizing so the view can reveal
        // the disclaimer and message actions early and show a follow-up placeholder.
        updateAssistantMessage({ blocks, pending: true, finalizing: true });
      },
    } satisfies StreamBufferCallbacks);
    activeStreamBuffer = streamBuffer;

    function applyCompletedAssistantMessage(blocks: Block[]): void {
      const payload = completionPayload;
      if (activeStreamBuffer === streamBuffer) activeStreamBuffer = null;
      // A silent probe that completed without ever materializing (e.g. a `done`
      // with no message events) produced no visible answer: leave the thread as is.
      if (!materialized) return;
      const completedMessage: Partial<ChatMessage> = {
        pending: false,
        finalizing: false,
        content: stringValue(payload?.text ?? streamText),
        blocks: normalizeBlocks(payload?.blocks) ?? blocks,
        sources: normalizeSources(payload?.sources),
        followUps: normalizeFollowUps(payload?.followUps),
        canGiveFeedback: payload?.canGiveFeedback === true,
      };
      const freezeLive = options.reconnect;
      state = {
        ...state,
        status: "idle",
        messages: state.messages.map((message) => {
          if (message.id === assistantMessageId) {
            return { ...message, ...completedMessage };
          }
          if (
            freezeLive &&
            message.fallback &&
            message.fallback.frozen === false
          ) {
            return {
              ...message,
              fallback: { ...message.fallback, frozen: true },
            };
          }
          return message;
        }),
      };
      persistCurrentSession();
      emit();
      void refreshHistory(state.context);
      scheduleSummaryTitleRefresh();
    }

    async function enterFallbackMode(question: string): Promise<void> {
      if (fallbackTriggered) return;
      fallbackTriggered = true;
      // A real or partial answer was produced: never replace it with a fallback panel.
      if (completionPayload || streamText) return;
      if (controller.signal.aborted) return;
      // Endpoint/method absent (un-redeployed host): keep a bottom error alert.
      if (!config.endpoints.search || !api.search) {
        state = {
          ...state,
          status: "error",
          error: stringValue(
            config.labels.assistantUnavailable,
            "AI is niet beschikbaar.",
          ),
        };
        updateAssistantMessage({ pending: false });
        emit();
        return;
      }
      let sources =
        state.messages.find((m) => m.id === assistantMessageId)?.sources ?? [];
      // User questions carry markdown (composer input); Jonas searches on plain
      // text, so strip the markers for the query while keeping the raw question
      // for display and for the AI re-ask on reconnect.
      const searchQuery = stripMarkdownForSearch(question);
      try {
        const results = await api.search({
          query: searchQuery,
          product: state.context.product,
          languageId: state.context.languageId,
          area: state.context.activation,
          numberOfSources: 5,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        sources = normalizeSources(mapJonasResultsToSources(results));
      } catch {
        sources = []; // search failed too: the panel shows its empty state
      }
      state = {
        ...state,
        status: "idle",
        error: "",
        // Mint a synthetic local-only token when no real platform session was
        // established (e.g. AI down before the session event), so the thread counts as
        // an open conversation for the rest of this tab's lifetime. It is never sent to
        // the platform and never persisted (see the isLocalFallbackToken guards).
        sessionToken: state.sessionToken || `local-fallback-${Date.now()}`,
      };
      updateAssistantMessage({
        pending: false,
        content: "",
        blocks: [],
        sources,
        followUps: [],
        canGiveFeedback: false,
        fallback: { question, query: searchQuery, frozen: false },
      });
      persistCurrentSession();
      emit();
      // Recovery is picked up when the visitor comes back to the tab; the reconnect
      // button and a fallback search cover the case where they never left.
      startVisibilityRecovery();
    }

    function restoreAfterFailedReconnect(errorMessage?: string): void {
      // Drop the temporary assistant placeholder; the existing live fallback element
      // stays trailing, so the thread remains in fallback mode (banner persists).
      // A transient failure (e.g. rate limit) also surfaces the bottom alert so the
      // user knows the retry failed, while the Help articles stay available.
      state = {
        ...state,
        status: errorMessage ? "error" : "idle",
        error: errorMessage ?? "",
        messages: state.messages.filter((m) => m.id !== assistantMessageId),
      };
      persistCurrentSession();
      emit();
    }

    const handlers: StreamHandlers = {
      onEvent(event) {
        if (event.event === "session") {
          const payload = parseEventData<{
            sessionToken?: string;
            scope?: Partial<ChatPageContext>;
          }>(event.data);
          state = {
            ...state,
            sessionToken: payload.sessionToken ?? state.sessionToken,
            context: payload.scope
              ? mergeContext(payload.scope)
              : state.context,
          };
          emit();
          persistCurrentSession();
          upsertPendingHistoryEntry();
          return;
        }
        if (event.event === "message-start") {
          materialize();
          const payload = parseEventData<{ messageId?: string }>(event.data);
          setAssistantMessageId(payload.messageId);
          return;
        }
        if (event.event === "message-update") {
          materialize();
          const payload = parseEventData<{
            messageId?: string;
            text?: string;
            blocks?: unknown[];
            sources?: unknown[];
          }>(event.data);
          setAssistantMessageId(payload.messageId);
          streamText = payload.text ?? "";
          state = { ...state, status: "streaming" };
          updateAssistantMessage({
            content: streamText,
            sources: normalizeSources(payload.sources),
          });
          streamBuffer.setTargetBlocks(normalizeBlocks(payload.blocks) ?? []);
          emit();
          return;
        }
        if (event.event === "message-finalizing") {
          materialize();
          const payload = parseEventData<{ messageId?: string }>(event.data);
          setAssistantMessageId(payload.messageId);
          // The visible answer is done; only the trailing follow-ups are still being
          // generated. Reveal the whole answer at once and flag the turn finalizing so
          // the disclaimer/actions appear and a follow-up placeholder shows, while the
          // turn stays pending until message-complete delivers the final payload.
          state = { ...state, status: "streaming" };
          streamBuffer.requestSettle();
          return;
        }
        if (event.event === "message-complete") {
          materialize();
          const payload = parseEventData<{
            messageId?: string;
            text?: string;
            blocks?: unknown[];
            followUps?: unknown[];
            sources?: unknown[];
            canGiveFeedback?: boolean;
          }>(event.data);
          setAssistantMessageId(payload.messageId);
          completionPayload = payload;
          state = { ...state, status: "streaming" };
          streamBuffer.complete(normalizeBlocks(payload.blocks) ?? []);
          return;
        }
        if (event.event === "done") {
          const payload = parseEventData<{ status?: string }>(event.data);
          if (!completionPayload && payload.status === "completed") {
            streamBuffer.complete();
          }
          return;
        }
        if (event.event === "error") {
          stopVisualBuffer(streamBuffer);
          const payload = parseEventData<{
            message?: string;
            code?: string;
            retryable?: boolean;
            outage?: boolean;
          }>(event.data);
          // The gateway says whether the assistant is unreachable beyond this request;
          // anything it does not flag stays a plain message, so a code we do not know
          // yet can never silently open the fallback.
          const isOutage = payload.outage === true;
          if (isOutage) {
            // The AI Platform is unavailable: enter fallback mode (Jonas search).
            if (silent && !materialized) {
              // Background probe and the platform is still down: fail invisibly and
              // leave the thread in fallback mode (no banner change, no flicker).
            } else if (options.reconnect) {
              restoreAfterFailedReconnect();
            } else {
              void enterFallbackMode(trimmed);
            }
          } else {
            // A chat the platform can no longer continue is terminal, so it has to reach
            // the visitor even when a background probe asked the question: swallowing it
            // would leave the probes chasing a chat that is never coming back.
            const closedChat = payload.code === "chat_unavailable";
            if (silent && !materialized && !closedChat) {
              // A background probe must never surface an alert the user did not ask
              // for; swallow the rejection and stay silently in fallback mode.
            } else {
              state = {
                ...state,
                status: "error",
                error: closedChat
                  ? stringValue(
                      config.labels.chatUnavailableNotice,
                      payload.message ?? "",
                    )
                  : (payload.message ?? "Chat request failed."),
              };
              updateAssistantMessage({ pending: false });
              emit();
            }
          }
        }
      },
    };

    try {
      const context = buildChatContext(state.context, trimmed);
      state = { ...state, context };
      // The AI Platform reads only the scope fields; page/source context is no
      // longer sent because the active prompt does not template those values.
      const requestContext = {
        product: context.product,
        languageId: context.languageId,
        culture: context.culture,
        activation: context.activation,
      };
      const attachmentPayload = normalizedAttachments.length
        ? { attachments: normalizedAttachments }
        : {};
      if (state.sessionToken && !isLocalFallbackToken(state.sessionToken)) {
        await api.continue(
          {
            sessionToken: state.sessionToken,
            message: trimmed,
            ...attachmentPayload,
            context: requestContext,
          },
          handlers,
          controller.signal,
        );
      } else {
        // When history is OFF, mint a fresh visitor identity before every new
        // session so that only the current chat appears once the user enables
        // history. Skipped when keepHistory is true: the server already links
        // the session to the persistent (30-day) identity. Also skipped when
        // startNewOffChat is absent so api.start is reached without an extra
        // microtask deferral (rotation would be a no-op anyway).
        if (!state.keepHistory && api.startNewOffChat) {
          await rotateOffIdentity();
        }
        await api.start(
          {
            message: trimmed,
            source: context.entryPoint || "workspace",
            ...attachmentPayload,
            context: requestContext,
          },
          handlers,
          controller.signal,
        );
      }
    } catch (error) {
      stopVisualBuffer(streamBuffer);
      if (!controller.signal.aborted) {
        if (silent && !materialized) {
          // Background probe failed (connection or HTTP error) before the platform
          // produced anything: stay silently in fallback mode, no alert, no flicker.
        } else if (options.reconnect) {
          // A failed reconnect keeps the user in fallback mode (Help articles stay
          // available) by dropping the placeholder; a transient HTTP failure also
          // surfaces the bottom alert so the user knows the retry failed.
          restoreAfterFailedReconnect(
            error instanceof ChatStreamError ? error.message : undefined,
          );
        } else if (error instanceof ChatStreamError) {
          // Transient HTTP failure (e.g. 429): bottom alert, never fallback.
          state = { ...state, status: "error", error: error.message };
          updateAssistantMessage({ pending: false });
          emit();
        } else {
          // Connection-level failure (network/app down): treat as an outage.
          await enterFallbackMode(trimmed);
        }
      }
    } finally {
      if (activeController === controller) activeController = null;
      if (silentProbeController === controller) silentProbeController = null;
      persistCurrentSession();
    }
  }

  function abortActiveStream(): void {
    // A new stream/scope change also supersedes any in-flight resume poll.
    resumePollSequence += 1;
    activeController?.abort();
    activeController = null;
    // Also cancel any in-flight background reconnect probe so it cannot materialize
    // an answer into a thread that was just reset (scope change / history switch).
    silentProbeController?.abort();
    silentProbeController = null;
    // Stop watching for a return to the tab and drop any in-flight fallback search: the
    // thread is being reset, so a late search response must not repopulate the old panel.
    stopVisibilityRecovery();
    fallbackSearchController?.abort();
    fallbackSearchController = null;
    activeStreamBuffer?.stop();
    activeStreamBuffer = null;
  }

  function mergeContext(
    patch: Partial<ChatPageContext> | Record<string, unknown>,
  ): ChatPageContext {
    return normalizeWorkspaceContext(
      {
        ...state.context,
        ...patch,
      },
      baseUrl,
    );
  }

  function updatePreparedContext(
    context: ChatPageContext,
  ): PreparedQuestionState | null {
    if (!state.preparedQuestion) return null;
    return {
      ...state.preparedQuestion,
      context: buildChatContext(
        {
          ...state.preparedQuestion.context,
          ...context,
        },
        state.preparedQuestion.question,
        state.preparedQuestion.entryPoint,
      ),
    };
  }

  async function refreshHistory(context: ChatPageContext): Promise<void> {
    if (!api.getHistory) {
      // No history endpoint wired: settle the panel so it never sticks on the
      // first-load skeleton.
      if (state.historyStatus !== "ready") {
        state = { ...state, historyStatus: "ready" };
        emit();
      }
      return;
    }
    const requestId = ++historyRequestSequence;
    // Show the skeleton only while there is nothing settled yet; a background
    // refresh of an already-loaded list keeps the current entries on screen.
    if (state.historyStatus !== "ready") {
      state = { ...state, historyStatus: "loading" };
      emit();
    }
    const scope = getWorkspaceScope(context);
    try {
      const result = await api.getHistory({
        product: scope.product,
        languageId: scope.languageId,
        activation: scope.activation,
        ...(state.sessionToken ? { activeSessionToken: state.sessionToken } : {}),
      });
      const history = Array.isArray(result)
        ? { items: result, keepHistory: false }
        : result;
      // Drop stale responses: only the most-recent refresh may apply, so a
      // slow older-scope request can never overwrite a newer-scope result.
      if (requestId !== historyRequestSequence) return;
      // Persist the authoritative value so the next page load seeds the toggle right.
      writeKeepHistoryPreference(history.keepHistory);
      state = {
        ...state,
        history: preserveKnownTitles(mapHistoryItems(history.items), state.history),
        keepHistory: history.keepHistory,
        historyStatus: "ready",
      };
      emit();
    } catch {
      // Drop stale failures so an old-scope error can't override a newer request.
      if (requestId !== historyRequestSequence) return;
      // History is a best-effort display list: keep the cached entries and the
      // last-known toggle, but surface the failure so the panel can offer a retry
      // instead of collapsing to a silent blank (e.g. on a 429 from rapid reloads).
      state = { ...state, historyStatus: "error" };
      emit();
    }
  }

  /**
   * Keeps only the handle to the running conversation, never its content: a reload
   * fetches the messages back from /api/chat/resume, which answers solely for the
   * visitor's own chat cookie. So a browser profile that is shared, backed up or
   * inspected afterwards holds no questions or answers (pentest finding).
   */
  function persistCurrentSession(): void {
    if (!historyStore || !state.sessionToken) return;
    // A synthetic fallback token lives only in this tab and the platform cannot resume
    // it, so a snapshot of it would restore nothing.
    if (isLocalFallbackToken(state.sessionToken)) return;
    historyStore.writeCurrentSession({
      sessionToken: state.sessionToken,
      sessionScope: getWorkspaceScope(state.context),
    });
  }

  /**
   * Puts the conversation in the panel the moment the platform hands us a token, rather than
   * only once the answer completes. Before this, stopping an answer or navigating away left a
   * chat that existed server-side but was absent from the list, so there was no way back to it.
   *
   * The platform names a fresh chat "Chat" until its summarization prompt runs, so the visitor's
   * own question is the better label; the next refresh replaces it with the real title.
   */
  function upsertPendingHistoryEntry(): void {
    const sessionToken = state.sessionToken;
    if (!sessionToken || !state.keepHistory) return;
    if (state.history.some((entry) => entry.sessionToken === sessionToken)) return;

    const title = questionTitle(
      state.messages.find((message) => message.role === "user")?.content,
    );
    if (!title) return;

    const nowIso = new Date().toISOString();
    state = {
      ...state,
      history: [
        { sessionToken, title, createdUtc: nowIso, updatedUtc: nowIso },
        ...state.history,
      ],
    };
    emit();
  }

  /**
   * The platform summarizes a conversation into its title shortly after the answer finishes, so
   * the refresh on completion is usually a moment too early and would leave the question standing
   * as the title for the rest of the session. Re-check a couple of times, and stop as soon as a
   * real title arrives so an unsummarized chat costs at most these two extra calls.
   */
  function scheduleSummaryTitleRefresh(): void {
    const sessionToken = state.sessionToken;
    if (!sessionToken || !state.keepHistory) return;

    for (const delay of SUMMARY_TITLE_REFRESH_DELAYS_MS) {
      setTimeout(() => {
        if (state.sessionToken !== sessionToken) return;
        const entry = state.history.find((e) => e.sessionToken === sessionToken);
        if (entry && isSummarizedTitle(entry, state.messages)) return;
        void refreshHistory(state.context);
      }, delay);
    }
  }

  function stopVisualBuffer(streamBuffer: StreamBuffer): void {
    if (activeStreamBuffer === streamBuffer) activeStreamBuffer = null;
    streamBuffer.stop();
  }

  async function submitFeedback(
    messageId: string,
    value: "positive" | "negative" | null,
  ): Promise<void> {
    const message = state.messages.find(
      (candidate) => candidate.id === messageId,
    );
    if (!message || !message.canGiveFeedback) return;
    // Feedback is toggleable: re-posting a different score overwrites the vote
    // (switch 👍↔👎) and null retracts it. Skip a no-op, and ignore a click while another
    // vote for this thread is still in flight (the thumbs are disabled then anyway).
    const previous = message.feedback ?? null;
    if (value === previous || state.feedbackPendingId) return;
    if (value === null && !api.clearFeedback) return;

    // Apply the styling immediately (optimistic) and mark the vote in-flight so the thumbs
    // disable: the rating feels instant while a double-click cannot fire a second request.
    state = {
      ...state,
      feedbackPendingId: messageId,
      messages: withFeedback(state.messages, messageId, value),
    };
    emit();

    try {
      if (value === null) {
        await api.clearFeedback!({
          sessionToken: state.sessionToken,
          messageId,
        });
      } else {
        const score = value === "positive" ? 100 : 1;
        await api.postFeedback({
          sessionToken: state.sessionToken,
          messageId,
          score,
        });
      }
      // Confirmed: keep the optimistic styling, just re-enable the thumbs.
      state = { ...state, feedbackPendingId: "" };
      emit();
    } catch {
      // Failed: revert the optimistic styling and re-enable the thumbs.
      state = {
        ...state,
        feedbackPendingId: "",
        messages: withFeedback(state.messages, messageId, previous),
      };
      emit();
    }
  }

  // Mint an encrypted, expiring share token for the current chat up to the given
  // message (the fork point). The caller turns the token into a shareable URL.
  async function createShare(messageId: string): Promise<ChatShareResponse> {
    if (!api.share) {
      throw new Error("Delen wordt niet ondersteund.");
    }
    return api.share({ sessionToken: state.sessionToken, messageId });
  }

  // Redeem a `#share=` link: copy the shared chat into a new chat owned by this visitor and show
  // it. The backend uses CopyChat, so no message is sent and no answer is generated. Mirrors
  // resumeHistoryEntry: a restoring placeholder first, then the copied conversation + session token.
  async function redeemShare(shareToken: string): Promise<boolean> {
    if (!api.redeem || !shareToken.trim()) return false;
    abortActiveStream();
    // The shared chat is independent of any article that was open, so close the source
    // panel (see resumeHistoryEntry) before showing the copy.
    sourceRequestSequence += 1;
    state = {
      ...state,
      status: "starting",
      sessionToken: "",
      preparedQuestion: null,
      isRestoring: true,
      messages: [createMessage("assistant", "", true)],
      error: "",
      activeSource: null,
      context: mergeContext({ selectedSourceUrl: "", sourceTitle: "" }),
    };
    emit();

    try {
      const response = await api.redeem(shareToken.trim());
      const responseContext = mergeContext({
        ...(response.context ?? {}),
        ...(response.scope ?? {}),
      });
      state = {
        ...state,
        status: "idle",
        sessionToken: stringValue(response.sessionToken),
        context: responseContext,
        isRestoring: false,
        messages: mapResumeMessages(response.messages, config.labels),
      };
      persistCurrentSession();
      emit();
      // Opening a share link redeems instead of bootstrapping, so the conversations
      // panel is otherwise never loaded on this path (it stays stuck on the initial
      // loading skeleton). Refresh it now, scoped to the redeemed chat, so the panel
      // settles and lists the freshly copied conversation with the active one
      // highlighted (GetHistory echoes back the active session token).
      void refreshHistory(state.context);
      return true;
    } catch (error) {
      state = {
        ...state,
        status: "error",
        sessionToken: "",
        isRestoring: false,
        error:
          error instanceof Error
            ? error.message
            : "Dit gedeelde gesprek kan niet worden geopend.",
        messages: [],
      };
      emit();
      return false;
    }
  }

  function prepareQuestion(
    question: string,
    options: ChatBootstrapOptions = {},
  ): boolean {
    const trimmed = question.trim();
    if (!trimmed) return false;

    const entryPoint = options.entryPoint || "query-url";
    const context = buildChatContext(
      mergeContext(options.context ?? {}),
      trimmed,
      entryPoint,
    );
    if (isCurrentActiveQuestion(trimmed, options, context)) {
      state = {
        ...state,
        status: "idle",
        context,
        preparedQuestion: null,
        isRestoring: false,
        error: "",
      };
      emit();
      persistCurrentSession();
      return true;
    }

    abortActiveStream();
    const preparedQuestion: PreparedQuestionState = {
      question: trimmed,
      entryPoint,
      context,
      historySessionToken: options.historySessionToken ?? "",
    };

    state = {
      ...state,
      status: "idle",
      sessionToken: "",
      messages: [],
      context,
      preparedQuestion,
      isRestoring: false,
      error: "",
    };
    emit();
    historyStore?.clearCurrentSession();
    return true;
  }

  function isCurrentActiveQuestion(
    question: string,
    options: ChatBootstrapOptions,
    context: ChatPageContext,
  ): boolean {
    if (!state.sessionToken && state.messages.length === 0) return false;
    const requestedSessionToken = stringValue(options.historySessionToken);
    if (!scopeMatches(context, state.context)) return false;
    if (requestedSessionToken === state.sessionToken) return true;

    return storedQuestionMatchesRequest(
      question,
      state.context.query || getFirstUserQuestion(),
    );
  }

  function getFirstUserQuestion(): string {
    return stringValue(
      state.messages.find((message) => message.role === "user")?.content,
    );
  }

  async function startPreparedQuestion(): Promise<void> {
    const preparedQuestion = state.preparedQuestion;
    if (!preparedQuestion) return;

    state = {
      ...state,
      context: preparedQuestion.context,
      preparedQuestion: null,
    };
    await submitQuestion(preparedQuestion.question);
  }

  // While the platform is down, recovery is driven by the visitor being there: the
  // "Opnieuw verbinden" button, a fallback search, and coming back to the tab. Nothing
  // probes on behalf of someone who is not looking, so there is no schedule to budget,
  // no background load per open tab, and no chain that can outlive its conversation.
  function startVisibilityRecovery(): void {
    if (visibilityRecoveryOff) return;
    visibilityRecoveryOff = onVisibilityChange(() => {
      if (isDocumentHidden()) return;
      if (!getLiveFallbackMessage(state.messages)?.fallback) {
        stopVisibilityRecovery();
        return;
      }
      void attemptReconnect(false);
    });
  }

  function stopVisibilityRecovery(): void {
    visibilityRecoveryOff?.();
    visibilityRecoveryOff = null;
  }

  // The single entry point for every AI reconnect attempt — the explicit "Opnieuw
  // verbinden" button (userInitiated), returning to the tab, and a fallback search
  // (both automatic). Funnelling all three through here gives us ONE in-flight guard
  // (state.reconnecting) so a second trigger can never start a competing stream while
  // one is running — that race is what made the backend reject the duplicate with its
  // one-stream-per-chat error and surfaced "Er loopt al een antwoord voor deze chat."
  //
  // Always re-asks the ORIGINAL failed question (never a search term). On success ask()
  // freezes the live fallback panel and appends the real answer. Automatic attempts are
  // fully silent (invisible on failure) and rate-limited; the button defers its reveal
  // the same way (so the green "weer beschikbaar" banner only shows on real recovery)
  // but still surfaces a transient failure as the bottom alert. The reconnecting flag
  // is held for at least RECONNECT_MIN_BUSY_MS so the banner's busy state reads as a
  // deliberate transition and a rapid second press stays blocked a moment longer.
  async function attemptReconnect(userInitiated: boolean): Promise<void> {
    const live = getLiveFallbackMessage(state.messages);
    if (!live?.fallback) return;
    // Shared guard: one reconnect at a time, no matter which trigger fired. Set
    // synchronously before the first await so a concurrent caller is reliably blocked.
    if (state.reconnecting) return;
    const startedAt = Date.now();
    // Throttle only the automatic triggers (poll + search); an explicit button press
    // must always be honoured immediately.
    if (
      !userInitiated &&
      startedAt - lastReconnectAt < reconnectThrottleMs
    ) {
      return;
    }
    lastReconnectAt = startedAt;
    setReconnecting(true);
    try {
      await ask(live.fallback.question, [], {
        suppressUserBubble: true,
        reconnect: true,
        // Automatic probes stay fully silent (no alert on failure); the button defers
        // its reveal but still surfaces transient errors.
        silent: !userInitiated,
        deferMaterialize: userInitiated,
      });
    } finally {
      // Hold the busy state for a minimum duration so a fast failure does not flicker
      // and the button cannot be re-triggered back-to-back.
      const elapsed = Date.now() - startedAt;
      if (elapsed < reconnectMinBusyMs) {
        await waitFor(reconnectMinBusyMs - elapsed);
      }
      setReconnecting(false);
    }
  }

  async function refineFallbackSearch(query: string): Promise<void> {
    const live = getLiveFallbackMessage(state.messages);
    const trimmed = query.trim();
    if (
      !live?.fallback ||
      !trimmed ||
      !config.endpoints.search ||
      !api.search ||
      state.status === "starting" ||
      state.status === "streaming"
    ) {
      return;
    }
    // Order guard: a newer search supersedes any in-flight one. Abort the previous
    // request and bump the sequence so a slower older response (which may resolve
    // after this one) can never apply its stale results over the newer query.
    fallbackSearchController?.abort();
    const requestId = ++fallbackSearchSequence;
    const controller = new AbortController();
    fallbackSearchController = controller;
    // True once a superseding search has started or this request was aborted: such a
    // stale request must touch nothing — not the results, and not the empty state.
    const isStale = () =>
      controller.signal.aborted || requestId !== fallbackSearchSequence;
    try {
      const results = await api.search({
        query: trimmed,
        product: state.context.product,
        languageId: state.context.languageId,
        area: state.context.activation,
        numberOfSources: 5,
        signal: controller.signal,
      });
      if (isStale()) return;
      const current = getLiveFallbackMessage(state.messages);
      if (!current?.fallback || current.id !== live.id) return;
      updateMessage(live.id, {
        sources: normalizeSources(mapJonasResultsToSources(results)),
        fallback: { ...current.fallback, query: trimmed },
      });
      persistCurrentSession();
    } catch {
      // An aborted/superseded request (incl. AbortError) leaves the newer search to
      // own the panel; never blank it on behalf of a request that no longer matters.
      if (isStale()) return;
      const current = getLiveFallbackMessage(state.messages);
      if (!current?.fallback || current.id !== live.id) return;
      updateMessage(live.id, {
        sources: [],
        fallback: { ...current.fallback, query: trimmed },
      });
      persistCurrentSession();
    } finally {
      if (fallbackSearchController === controller) fallbackSearchController = null;
    }
    // The search applied (or failed and showed its empty state) and we are still on
    // the same live fallback element, so silently re-probe the AI Platform with the
    // original question. Placed AFTER the try/finally so any early return above
    // (stale/aborted search, or the live element changed/froze meanwhile) skips it.
    await attemptReconnect(false);
  }

  async function reconnectAi(): Promise<void> {
    // The explicit "Opnieuw verbinden met Jonas (AI)" button. Routed through the single
    // guarded entry point: if a background probe (or an earlier press) is already in
    // flight, state.reconnecting blocks this one instead of starting a competing stream
    // that the backend would reject. The button is also disabled in the UI while
    // reconnecting, so this is the belt-and-braces guard for programmatic callers.
    await attemptReconnect(true);
  }

  async function submitQuestion(
    question: string,
    attachments: ChatAttachmentInput[] = [],
  ): Promise<void> {
    return ask(question, attachments, {});
  }

  function updateContext(context: Partial<ChatPageContext>): void {
    const nextContext = mergeContext(context);
    state = {
      ...state,
      context: nextContext,
      preparedQuestion: updatePreparedContext(nextContext),
    };
    emit();
  }

  function changeWorkspaceScope(
    patch: WorkspaceScopePatch,
    options: { preserveConversation?: boolean; navigating?: boolean } = {},
  ): boolean {
    // Preservation is only safe for a language-only change. Product and activation are the
    // immutable KB scope of a conversation, so a patch that touches them must ALWAYS reset the
    // chat — even if the caller passed preserveConversation — otherwise the kept sessionToken
    // (old scope) desyncs from the new UI scope and a follow-up is rejected with invalid_scope.
    const isLanguageOnlyPatch =
      patch.product === undefined && patch.activation === undefined;
    const preserveConversation =
      options.preserveConversation === true && isLanguageOnlyPatch;
    const hadChatContent =
      !!state.sessionToken || state.messages.length > 0 || state.isRestoring;
    abortActiveStream();
    sourceRequestSequence += 1;

    const nextContext = mergeContext({
      ...patch,
      selectedSourceUrl: "",
      sourceTitle: "",
    });
    const nextState: ChatState = {
      ...state,
      status: "idle",
      error: "",
      retryQuestion: "",
      activeSource: null,
      scope: updateScopeConfig(state.scope, patch),
      context: nextContext,
      preparedQuestion: updatePreparedContext(nextContext),
      isRestoring: false,
    };

    // A language-only switch KEEPS the conversation: the AI Platform supports ContinueChat in
    // another language and restoreCurrentSession adopts the page's new language on reload, so the
    // chat survives (and continues in the new language) instead of being closed. Product/country
    // switches change the immutable KB scope, so they still reset the conversation.
    if (hadChatContent && !preserveConversation) {
      nextState.sessionToken = "";
      nextState.messages = [];
      nextState.preparedQuestion = null;
      historyStore?.clearCurrentSession();
    }

    state = nextState;
    emit();
    // A scope change that is immediately followed by a full-page navigation (every option with a
    // server-rendered URL) must NOT start a history fetch: the reload re-fetches server-side, and
    // an in-flight fetch aborted by the navigation renders a red "no connection" error in Firefox
    // (Chrome discards the rejection before repaint; Firefox shows it first). Issue #116.
    if (!options.navigating) {
      void refreshHistory(nextContext);
    }
    return true;
  }

  function closeSource(): void {
    sourceRequestSequence += 1;
    const nextContext = mergeContext({
      selectedSourceUrl: "",
      sourceTitle: "",
    });
    state = {
      ...state,
      activeSource: null,
      context: nextContext,
      preparedQuestion: updatePreparedContext(nextContext),
    };
    emit();
    persistCurrentSession();
  }

  function resetSession(): void {
    abortActiveStream();
    sourceRequestSequence += 1;
    const nextContext = mergeContext({
      query: "",
      entryPoint: "workspace",
      selectedSourceUrl: "",
      sourceTitle: "",
    });
    state = {
      ...state,
      status: "idle",
      sessionToken: "",
      messages: [],
      activeSource: null,
      context: nextContext,
      preparedQuestion: null,
      isRestoring: false,
      error: "",
      retryQuestion: "",
    };
    historyStore?.clearCurrentSession();
    emit();
  }

  function clearPreparedQuestion(): void {
    if (!state.preparedQuestion) return;
    state = {
      ...state,
      preparedQuestion: null,
      isRestoring: false,
      error: "",
    };
    emit();
  }

  async function resumeFromHistory(index: number): Promise<boolean> {
    if (!api.resume) return false;
    const entry = state.history[index];
    if (!entry?.sessionToken) return false;
    const ok = await resumeHistoryEntry(entry);
    // Mirror the bootstrap pattern: if the loaded conversation ends with a
    // trailing user question (the answer was still generating when the user
    // last left), poll until the server-persisted answer appears — the same
    // way bootstrap recovers a mid-stream refresh.
    if (ok && hasPendingAnswerToResume()) {
      await resumePendingAnswer();
    }
    return ok;
  }

  async function clearHistory(): Promise<void> {
    if (!api.resetHistory) return;
    try {
      await api.resetHistory();
      // Drop any in-flight history refresh so it can't repopulate after clearing.
      historyRequestSequence += 1;
      state = { ...state, history: [] };
      emit();
    } catch {
      // Keep the current list on failure; clearing is best-effort.
    }
  }

  async function removeHistoryEntry(
    sessionToken: string,
  ): Promise<RemoveHistoryEntryResult> {
    if (!api.removeHistoryItem || !sessionToken) return "error";
    try {
      await api.removeHistoryItem(sessionToken);
      // Prevent an older refresh from restoring the entry after deletion.
      historyRequestSequence += 1;
      state = {
        ...state,
        history: state.history.filter(
          (entry) => entry.sessionToken !== sessionToken,
        ),
      };
      emit();
      return "removed";
    } catch (error) {
      if (error instanceof ChatHistoryRemovalError) {
        if (error.status === 429) return "rate-limited";
        if (error.status === 404 || error.retryable === false) {
          return "not-found";
        }
        if (error.retryable === true || error.status >= 500) {
          return "retryable-error";
        }
      }
      return "error";
    }
  }

  async function setHistoryPreference(keepHistory: boolean): Promise<void> {
    if (!api.setHistoryPreference) return;
    const previous = state.keepHistory;
    // Optimistically reflect the new state so the toggle does not lag.
    state = { ...state, keepHistory };
    if (!keepHistory) {
      // OFF immediately hides the list; the current chat keeps running on the cookie.
      historyRequestSequence += 1;
      state = { ...state, history: [] };
    }
    emit();
    try {
      const confirmed = await api.setHistoryPreference(keepHistory);
      // Persist the confirmed value so the toggle shows it on the next load.
      writeKeepHistoryPreference(confirmed);
      state = { ...state, keepHistory: confirmed };
      emit();
      // ON adopts the current chat into the list; refresh to show it.
      if (confirmed) await refreshHistory(state.context);
    } catch {
      // Revert to the real state on failure; the cookie was not changed.
      state = { ...state, keepHistory: previous };
      emit();
    }
  }

  async function rotateOffIdentity(): Promise<void> {
    if (!api.startNewOffChat) return;
    try {
      await api.startNewOffChat();
    } catch {
      // Best-effort: if rotation fails the caller still resets the local session.
    }
  }

  async function resumeHistoryEntry(
    entry: ChatHistoryStateEntry,
  ): Promise<boolean> {
    if (!api.resume || !entry.sessionToken) return false;
    abortActiveStream();
    // The open article belongs to the conversation being left, so switching closes the
    // source panel. The bump states that intent for a fetch still in flight; the url
    // guard in isCurrentSourceRequest already drops one once activeSource is null.
    sourceRequestSequence += 1;
    state = {
      ...state,
      status: "starting",
      sessionToken: "",
      preparedQuestion: null,
      isRestoring: true,
      messages: [createMessage("assistant", "", true)],
      error: "",
      activeSource: null,
      context: mergeContext({ selectedSourceUrl: "", sourceTitle: "" }),
    };
    emit();

    try {
      const response = await api.resume(entry.sessionToken);
      const responseContext = mergeContext({
        ...(response.context ?? {}),
        ...(response.scope ?? {}),
      });
      const resumed = mapResumeMessages(response.messages, config.labels);
      // A stopped answer is settled, so present it as such straight from this response
      // instead of letting the pending-answer poll chase a turn that was never saved.
      const messages =
        normalizeConversationStatus(response.conversationStatus) === "cancelled"
          ? markAnswerStopped(resumed)
          : resumed;
      state = {
        ...state,
        status: messages === resumed ? "idle" : "stopped",
        sessionToken: stringValue(response.sessionToken),
        context: responseContext,
        isRestoring: false,
        retryQuestion: "",
        messages,
      };
      persistCurrentSession();
      emit();
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof ChatResumeError && error.status === 429
          ? config.labels.resumeRateLimited || config.labels.resumeFailed
          : error instanceof Error
            ? error.message
            : config.labels.resumeFailed;
      state = {
        ...state,
        status: "error",
        sessionToken: "",
        isRestoring: false,
        error: errorMessage,
        messages: [],
      };
      emit();
      return false;
    }
  }

  function hasPendingAnswerToResume(): boolean {
    if (typeof api.resume !== "function" || !state.sessionToken) return false;
    // A synthetic local-fallback token is never resumable against the platform.
    if (isLocalFallbackToken(state.sessionToken)) return false;
    const last = state.messages[state.messages.length - 1];
    if (!last) return false;
    // Two interrupted shapes resume: a trailing user turn (the empty placeholder
    // answer was dropped) or a trailing assistant still flagged pending (the
    // streamed-so-far partial was kept). Both poll until the full answer lands.
    return (
      last.role === "user" ||
      (last.role === "assistant" && last.pending === true)
    );
  }

  function applyStoppedAnswer(): void {
    state = {
      ...state,
      status: "stopped",
      isRestoring: false,
      error: "",
      retryQuestion: "",
      messages: markAnswerStopped(state.messages),
    };
    persistCurrentSession();
    emit();
  }

  async function resumePendingAnswer(): Promise<void> {
    // Bind to the api instance before the poll loop: the production client's resume()
    // reads this.config, so an extracted, unbound reference throws before any fetch and
    // the catch inside the loop silently swallows it, stalling recovery until the user
    // continues from history. Every other call site (start/continue/resumeFromHistory)
    // uses method syntax and keeps its receiver; this is the one extraction, so bind it.
    const resume = api.resume?.bind(api);
    const sessionToken = state.sessionToken;
    if (!resume || !sessionToken) return;

    // Two interrupted shapes reach here (see hasPendingAnswerToResume): a trailing
    // user turn whose empty answer placeholder was dropped, or a trailing assistant
    // still flagged pending — the streamed-so-far partial that was kept visible.
    const last = state.messages[state.messages.length - 1];
    const resumingPartial =
      !!last && last.role === "assistant" && last.pending === true;

    // Baseline counts only COMPLETED assistant turns. The kept partial is client-only
    // (the platform persists an assistant turn just at completion), so counting it
    // would mean the finished answer never exceeds the baseline and is never detected.
    // Counting completed assistants — not "is the last message an assistant" — also
    // keeps a continue refresh safe: GetChat can momentarily return the chat without
    // the just-asked follow-up (its trailing message is then the OLD answer), which
    // must not be mistaken for the new answer and drop the question.
    const baselineAssistantCount = state.messages.filter(
      (message) => message.role === "assistant" && message.pending !== true,
    ).length;
    const pollId = ++resumePollSequence;
    state = {
      ...state,
      status: "starting",
      isRestoring: true,
      error: "",
      // Keep the streamed partial as the visible "wordt voltooid" turn; otherwise add
      // an empty loader placeholder for the dropped-empty-answer case.
      messages: resumingPartial
        ? state.messages
        : [...state.messages, createMessage("assistant", "", true)],
    };
    emit();

    // Before a Pending status is observed, retain the bounded legacy schedule so a
    // temporarily stale Ready snapshot or an older platform response gets a short
    // projection grace period. Once the SDK reports Pending, repeat the final delay
    // until the answer or a terminal status arrives instead of guessing that every
    // generation finishes within the old ~30-second budget.
    let pollIndex = 0;
    let sawPendingStatus = false;
    let pendingWaitMs = 0;
    let readyGracePolls = 0;
    while (pollIndex < resumePollDelaysMs.length || sawPendingStatus) {
      const configuredDelay =
        resumePollDelaysMs[
          Math.min(pollIndex, resumePollDelaysMs.length - 1)
        ] ?? 0;
      const delay =
        pollIndex < resumePollDelaysMs.length
          ? configuredDelay
          : Math.max(configuredDelay, 1000);
      if (
        sawPendingStatus &&
        pendingWaitMs + delay > resumePendingMaxWaitMs
      ) {
        break;
      }
      if (delay > 0) await waitFor(delay);
      if (sawPendingStatus) pendingWaitMs += delay;
      pollIndex += 1;
      if (
        pollId !== resumePollSequence ||
        state.sessionToken !== sessionToken
      ) {
        return;
      }
      let response: ChatResumeResponse;
      try {
        response = await resume(sessionToken);
      } catch {
        // Transient failure (e.g. a network hiccup); keep polling within budget.
        continue;
      }
      if (
        pollId !== resumePollSequence ||
        state.sessionToken !== sessionToken
      ) {
        return;
      }
      const mapped = mapResumeMessages(response.messages, config.labels);
      const mappedAssistantCount = mapped.filter(
        (message) => message.role === "assistant",
      ).length;
      if (mapped.length > 0 && mappedAssistantCount > baselineAssistantCount) {
        const responseContext = mergeContext({
          ...(response.context ?? {}),
          ...(response.scope ?? {}),
        });
        state = {
          ...state,
          status: "idle",
          isRestoring: false,
          error: "",
          sessionToken: stringValue(response.sessionToken) || sessionToken,
          context: responseContext,
          messages: mapped,
        };
        persistCurrentSession();
        emit();
        return;
      }

      const conversationStatus = normalizeConversationStatus(
        response.conversationStatus,
      );
      if (conversationStatus === "cancelled") {
        // The visitor stopped this answer themselves. That is a finished turn, not a
        // failure, so restore the "answer stopped" bubble instead of polling on for an
        // answer the platform will never persist.
        applyStoppedAnswer();
        return;
      }
      if (conversationStatus === "error") {
        break;
      }
      if (conversationStatus === "pending") {
        sawPendingStatus = true;
        readyGracePolls = 0;
        continue;
      }
      if (sawPendingStatus) {
        // Message and chat projections can settle a fraction apart. Give Ready one
        // extra read before treating a terminal status without an answer as failure.
        if (conversationStatus === "ready" && readyGracePolls === 0) {
          readyGracePolls += 1;
          continue;
        }
        break;
      }
    }

    if (pollId !== resumePollSequence) return;
    // Budget exhausted. Keep a streamed partial visible and flag it incomplete (the
    // UI shows a subtle "mogelijk onvolledig" notice and the snapshot stays resumable,
    // so a later refresh re-polls). For the dropped-empty case there is no partial to
    // keep, so drop the loader and surface a retry notice. The retryQuestion field
    // carries the unanswered question so the UI can offer a one-click retry button;
    // submitQuestion clears both error and retryQuestion when the visitor asks again.
    if (resumingPartial) {
      state = {
        ...state,
        status: "idle",
        isRestoring: false,
        messages: markTrailingAssistantIncomplete(state.messages),
      };
    } else {
      const droppedMessages = dropTrailingEmptyAssistant(state.messages);
      const unanswered = droppedMessages.at(-1);
      const retryQuestion =
        unanswered?.role === "user" ? unanswered.content : "";
      state = {
        ...state,
        status: "idle",
        isRestoring: false,
        messages: droppedMessages,
        retryQuestion,
        error: retryQuestion
          ? "Het antwoord kon niet worden geladen."
          : "Het antwoord kon niet worden geladen. Stel je vraag opnieuw.",
      };
    }
    emit();
  }

  /**
   * Brings the conversation this tab was in back after a reload. The snapshot holds only the
   * handle to it, so the messages are fetched from the platform over the same resume path the
   * conversation panel uses. That costs one request per workspace reload and keeps every
   * question and answer out of browser storage.
   */
  async function restoreCurrentSession(
    options: {
      question?: string;
      context?: ChatPageContext;
      historySessionToken?: string | undefined;
    } = {},
  ): Promise<boolean> {
    const resume = api.resume?.bind(api);
    const snapshot = historyStore?.readCurrentSession();
    if (!resume || !snapshot?.sessionToken) return false;
    const targetContext = options.context ?? state.context;
    // Option 3: the active conversation may be in a different language than the page
    // (the platform supports ContinueChat in another language). Scope the restore on
    // product (+ country) only so such a chat is not dropped on reload; language is left
    // to the user, so a later continue simply runs in the page's current language.
    if (!scopeMatchesIgnoringLanguage(snapshot.sessionScope, targetContext)) {
      return false;
    }

    // Any user action that supersedes a resume poll (a question, stop, scope change,
    // switching conversations) also supersedes this restore, so it bails instead of
    // painting the old conversation over what the visitor just started.
    const restoreSequence = resumePollSequence;
    const previousMessages = state.messages;
    state = {
      ...state,
      status: "starting",
      isRestoring: true,
      error: "",
      messages: [createMessage("assistant", "", true)],
    };
    emit();

    let response: ChatResumeResponse;
    try {
      response = await resume(snapshot.sessionToken);
    } catch {
      // The conversation could not be fetched (expired token, rate limit, network
      // hiccup). Keep the snapshot so the next load can try again and fall back to a
      // clean slate rather than an error the visitor never asked for.
      abandonRestore(previousMessages);
      return false;
    }
    if (restoreSequence !== resumePollSequence) return false;

    const resumed = mapResumeMessages(response.messages, config.labels);
    if (resumed.length === 0) {
      abandonRestore(previousMessages);
      return false;
    }
    if (
      options.question &&
      !resumedMatchesQuestion(snapshot.sessionToken, resumed, options)
    ) {
      // This page was opened with a different question, so the stored conversation is
      // not what is being asked for; the caller prepares the new question instead.
      abandonRestore(previousMessages);
      return false;
    }

    // A stopped answer is settled, so present it as such straight from this response
    // instead of letting the pending-answer poll chase a turn that was never saved.
    const conversationStatus = normalizeConversationStatus(
      response.conversationStatus,
    );
    const messages =
      conversationStatus === "cancelled" ? markAnswerStopped(resumed) : resumed;
    restoredWithPendingAnswer = conversationStatus === "pending";
    state = {
      ...state,
      status: messages === resumed ? "idle" : "stopped",
      sessionToken: stringValue(response.sessionToken) || snapshot.sessionToken,
      messages,
      context: mergeContext({
        ...(response.context ?? {}),
        ...(response.scope ?? {}),
        // Language is user-driven: adopt the page's CURRENT language into the restored
        // conversation so a follow-up (continue) runs in the language the user switched to,
        // not the language the chat was originally started in. Product/country come from the
        // snapshot (they already matched via scopeMatchesIgnoringLanguage above).
        culture: targetContext.culture,
        languageId: targetContext.languageId,
      }),
      preparedQuestion: null,
      isRestoring: false,
      error: "",
    };
    emit();
    persistCurrentSession();
    return true;
  }

  // Undoes the loader that the restore attempt put on screen, so the caller can show the
  // home slate or the prepared question as if no restore had been attempted.
  function abandonRestore(previousMessages: ChatMessage[]): void {
    state = {
      ...state,
      status: "idle",
      isRestoring: false,
      error: "",
      messages: previousMessages,
    };
    emit();
  }

  function resumedMatchesQuestion(
    sessionToken: string,
    messages: ChatMessage[],
    options: {
      question?: string;
      historySessionToken?: string | undefined;
    },
  ): boolean {
    // An in-workspace navigation carries the token of the conversation it belongs to,
    // which settles it without looking at the wording.
    if (stringValue(options.historySessionToken) === sessionToken) return true;
    return storedQuestionMatchesRequest(
      stringValue(options.question),
      getFirstUserQuestionFrom(messages),
    );
  }

  function getFirstUserQuestionFrom(messages: ChatMessage[]): string {
    return stringValue(
      messages.find((message) => message.role === "user")?.content,
    );
  }

  return {
    async bootstrap(options: ChatBootstrapOptions = {}): Promise<void> {
      // The rest of the first paint is decided SYNCHRONOUSLY, before any network work,
      // so render #1 already shows the right thing instead of a transient Home (hero
      // text + centred composer) that jumps once the network resolves. Restoring the
      // conversation is the exception: the snapshot holds only its token, so the
      // messages come from the platform. It paints its own loader while that runs.
      const startupQuestion = options.question?.trim() ?? "";
      if (startupQuestion) {
        const startupContext = buildChatContext(
          mergeContext(options.context ?? {}),
          startupQuestion,
          options.entryPoint || "query-url",
        );
        const restored = await restoreCurrentSession({
          question: startupQuestion,
          context: startupContext,
          historySessionToken: options.historySessionToken,
        });
        if (!restored) prepareQuestion(startupQuestion, options);
      } else if (
        (!options.sourceUrl && !initial.selectedSourceUrl) ||
        options.historySessionToken
      ) {
        // A reload can carry an open source: the visitor opened an article from
        // inside an active chat session, then refreshed. pushSourceHistory stamps
        // that history entry with the conversation's session token, so a present
        // historySessionToken means "in-workspace source" rather than a fresh
        // article deep-link. Restore the conversation in that case
        // (restoreCurrentSession is scope-guarded and a no-op without a matching
        // snapshot) so the chat is not silently dropped behind the reopened source
        // panel. A genuine direct-article deep-link has no client history state and
        // keeps its clean Home slate.
        await restoreCurrentSession();
      }

      const sourceUrl = options.sourceUrl || initial.selectedSourceUrl;
      const sourceTitle =
        options.sourceTitle || initial.sourceTitle || sourceUrl || "";
      if (
        sourceUrl &&
        (options.sourceUrl ||
          initial.entryPoint === "source-url" ||
          initial.entryPoint === "direct-article")
      ) {
        const prerenderedSourceHtml = stringValue(
          options.prerenderedSourceHtml,
        );
        if (prerenderedSourceHtml) {
          adoptPrerenderedSource(sourceUrl, sourceTitle, prerenderedSourceHtml);
        } else {
          await openSource(sourceUrl, sourceTitle);
        }
      }

      // The history sidebar is independent of which conversation is shown, so load
      // it only after the initial layout is settled. This keeps it off the
      // first-paint critical path (no flash, no layout jump).
      await refreshHistory(state.context);

      // A reload can interrupt an answer that is still being generated. The
      // restored session then ends with the visitor's question and no answer; poll
      // the server until the persisted answer appears (self-healing across repeated
      // refreshes, since each load re-detects and restarts the bounded poll).
      if (restoredWithPendingAnswer || hasPendingAnswerToResume()) {
        await resumePendingAnswer();
      }
    },
    getState,
    subscribe(listener) {
      listeners.add(listener);
      listener(getState());
      return () => {
        listeners.delete(listener);
      };
    },
    submitQuestion,
    refineFallbackSearch,
    reconnectAi,
    prepareQuestion,
    clearPreparedQuestion,
    startPreparedQuestion,
    updateContext,
    changeWorkspaceScope,
    resumeFromHistory,
    clearHistory,
    removeHistoryEntry,
    refreshHistory: () => refreshHistory(state.context),
    setHistoryPreference,
    rotateOffIdentity,
    submitFeedback,
    createShare,
    redeemShare,
    openSource,
    closeSource,
    resetSession,
    stop() {
      const tokenToCancel = state.sessionToken;
      if (
        activeController &&
        tokenToCancel &&
        !isLocalFallbackToken(tokenToCancel)
      ) {
        // Explicit user stop: ask the gateway to cancel server-side generation so the
        // platform stops the in-flight answer instead of finishing it in the background.
        // Best-effort and fire-and-forget — the local stream is aborted below regardless.
        // A page refresh does NOT call this, so its answer persists and resume recovers it.
        void api.cancel?.(tokenToCancel)?.catch(() => {});
      }
      resumePollSequence += 1;
      activeController?.abort();
      activeController = null;
      activeStreamBuffer?.stop();
      activeStreamBuffer = null;
      state = {
        ...state,
        status: "stopped",
        retryQuestion: "",
        messages: state.messages.map((message) => {
          // Flag the in-flight assistant turn (an empty placeholder or a partially
          // streamed answer) as canceled so the view renders a localized "answer
          // stopped" notice in place of the message actions instead of an empty bubble.
          if (message.role === "assistant" && message.pending) {
            return { ...message, pending: false, canceled: true };
          }
          return { ...message, pending: false };
        }),
      };
      emit();
      // A stopped answer still leaves a conversation behind, so bring the list in line with
      // the server: it now knows the chat and may already have summarized the partial answer.
      void refreshHistory(state.context);
    },
  };
}

/**
 * A title counts as summarized once it is neither the platform placeholder nor the visitor's own
 * question, which is what the backend falls back to while the summarization prompt has not run.
 */
function isSummarizedTitle(
  entry: ChatHistoryStateEntry,
  messages: ChatMessage[],
): boolean {
  if (isPlaceholderTitle(entry.title)) return false;
  const question = questionTitle(
    messages.find((message) => message.role === "user")?.content,
  );
  return entry.title !== question;
}

/**
 * Keeps a title already on screen when the server still reports a placeholder, so a refresh
 * during the summarization window cannot replace a readable question with "Chat".
 */
function preserveKnownTitles(
  next: ChatHistoryStateEntry[],
  previous: ChatHistoryStateEntry[],
): ChatHistoryStateEntry[] {
  const known = new Map(
    previous
      .filter((entry) => !isPlaceholderTitle(entry.title))
      .map((entry) => [entry.sessionToken, entry.title] as const),
  );
  return next.map((entry) => {
    const kept = known.get(entry.sessionToken);
    return isPlaceholderTitle(entry.title) && kept
      ? { ...entry, title: kept }
      : entry;
  });
}

function mapHistoryItems(items: ChatHistoryItem[]): ChatHistoryStateEntry[] {
  if (!Array.isArray(items)) return [];
  return items.flatMap((item) => {
    if (!isRecord(item)) return [];
    const sessionToken = stringValue(item.sessionToken);
    if (!sessionToken) return [];
    const entry: ChatHistoryStateEntry = {
      sessionToken,
      title: stringValue(item.title),
      createdUtc: stringValue(item.createdUtc),
      updatedUtc: stringValue(item.updatedUtc),
    };
    return [entry];
  });
}

function createMessage(
  role: "user" | "assistant",
  content: string,
  pending: boolean,
  attachments: ChatAttachmentInput[] = [],
): ChatMessage {
  return {
    id: `${role}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    role,
    content,
    blocks: [],
    createdUtc: new Date().toISOString(),
    pending,
    sources: [],
    followUps: [],
    canGiveFeedback: false,
    attachments,
  };
}

function buildChatContext(
  context: ChatPageContext,
  question: string,
  entryPoint = context.entryPoint,
): ChatPageContext {
  return normalizeWorkspaceContext({
    ...context,
    query: question || context.query,
    entryPoint: entryPoint || context.entryPoint || "workspace",
  });
}

function normalizeWorkspaceContext(
  value:
    | InitialWorkspaceState
    | Partial<ChatPageContext>
    | Record<string, unknown>,
  baseUrl = getCurrentBaseUrl(),
): ChatPageContext {
  const selectedSourceUrl = getSafeSourceUrl(
    stringValue(value.selectedSourceUrl),
    baseUrl,
  );
  const languageId = normalizeLanguageId(
    String(value.languageId ?? ""),
    String(value.culture ?? ""),
  );
  const context: ChatPageContext = {
    product: stringValue(value.product),
    languageId,
    culture: stringValue(value.culture, "nl"),
    activation: stringValue(value.activation),
    query: stringValue(
      "initialQuestion" in value && value.initialQuestion
        ? value.initialQuestion
        : value.query,
    ),
    entryPoint: stringValue(value.entryPoint, "workspace"),
    selectedSourceUrl,
    sourceTitle: selectedSourceUrl ? stringValue(value.sourceTitle) : "",
    filters: normalizeFiltersValue(value.filters),
  };

  return context;
}

function createDefaultSessionHistoryStore(): SessionHistoryStore | null {
  if (typeof window === "undefined") return null;

  try {
    return createSessionHistoryStore({
      sessionStorage: window.sessionStorage,
    });
  } catch {
    return null;
  }
}

function updateScopeConfig(
  scope: ChatState["scope"],
  patch: WorkspaceScopePatch,
): ChatState["scope"] {
  const nextScope: ChatState["scope"] = {
    ...scope,
    language:
      typeof patch.culture === "string"
        ? updateScopeGroup(scope.language, patch.culture)
        : scope.language,
  };
  if (scope.product) {
    nextScope.product =
      typeof patch.product === "string"
        ? updateScopeGroup(scope.product, patch.product)
        : scope.product;
  }
  return nextScope;
}

function updateScopeGroup(
  group: ChatState["scope"]["language"],
  value: string,
): ChatState["scope"]["language"] {
  const option = [group.current, ...group.options].find(
    (candidate) => candidate.value === value,
  ) ?? { value, label: value };
  return {
    current: { ...option, current: true },
    options: group.options.map((candidate) => ({
      ...candidate,
      current: candidate.value === value,
    })),
  };
}

function copyContext(context: ChatPageContext): ChatPageContext {
  return {
    ...context,
    filters: context.filters.map((filter) => ({ ...filter })),
  };
}

function copyPreparedQuestion(
  preparedQuestion: PreparedQuestionState,
): PreparedQuestionState {
  return {
    ...preparedQuestion,
    context: copyContext(preparedQuestion.context),
  };
}

function mapResumeMessages(
  messages: ChatResumeMessage[] | undefined,
  labels: ChatConfig["labels"],
): ChatMessage[] {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const mapped = messages.map((message): ChatMessage => {
    const role = message.role === "user" ? "user" : "assistant";
    const content = stringValue(message.text || message.content);
    const id =
      stringValue(message.messageId || message.id) ||
      `${role}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    // A resumed turn is normally complete. These flags cover a platform that hands back
    // a turn it is still generating: honouring them keeps the answer-so-far visible and
    // lets the resume poll run on until the full one lands.
    const pending = role === "assistant" && message.pending === true;
    const incomplete = role === "assistant" && message.incomplete === true;
    // A user-stopped turn resumes as canceled; restore the flag so the cancellation
    // notice (not an empty bubble) is shown and no resume poll is triggered.
    const canceled = role === "assistant" && message.canceled === true;
    // Attachments are request-only, so a resumed user turn carries at most metadata and
    // renders as a non-previewable chip rather than vanishing.
    const restoredAttachments =
      role === "user" ? normalizeResumeAttachments(message.attachments) : [];
    return {
      id,
      role,
      content,
      blocks:
        role === "assistant" ? (normalizeBlocks(message.blocks) ?? []) : [],
      createdUtc: stringValue(
        message.createdUtc || message.createdAtUtc || message.timestamp,
        new Date().toISOString(),
      ),
      pending,
      ...(incomplete ? { incomplete: true } : {}),
      ...(canceled ? { canceled: true } : {}),
      ...(restoredAttachments.length
        ? { attachments: restoredAttachments }
        : {}),
      sources: normalizeSources(message.sources),
      followUps: normalizeFollowUps(message.followUps),
      canGiveFeedback:
        role === "assistant" &&
        !pending &&
        (message.canGiveFeedback === true || !!message.messageId),
      ...normalizeFeedbackValue(message.feedback, message.score),
      ...(isRecord(message.fallback)
        ? { fallback: message.fallback as FallbackState }
        : {}),
    };
  });
  return dropTrailingEmptyAssistant(mapped);
}

function dropTrailingEmptyAssistant(messages: ChatMessage[]): ChatMessage[] {
  // A forced refresh while an answer is streaming can leave the platform with a
  // placeholder assistant turn that never received text or blocks.
  // Restoring it verbatim renders an empty, stuck answer bubble, so drop a trailing
  // assistant message that produced no content. A partial that DID stream content is
  // kept (it is non-empty) and recovered via the resume poll instead.
  // A fallback element also has empty content+blocks but renders from fallback+sources:
  // never drop it.
  const last = messages[messages.length - 1];
  if (
    last &&
    last.role === "assistant" &&
    !last.content &&
    last.blocks.length === 0 &&
    !last.fallback && // a fallback panel renders from fallback+sources, not content/blocks
    !last.canceled // a canceled turn renders the cancellation notice, not content/blocks
  ) {
    return messages.slice(0, -1);
  }
  return messages;
}

function markTrailingAssistantIncomplete(
  messages: ChatMessage[],
): ChatMessage[] {
  // Stop the "wordt voltooid" indicator (pending: false) but keep the streamed
  // partial visible and flagged incomplete so the UI shows a subtle "mogelijk
  // onvolledig" notice.
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant") return messages;
  return [
    ...messages.slice(0, -1),
    { ...last, pending: false, incomplete: true },
  ];
}

// The platform keeps no assistant turn for an answer the visitor stopped, so a chat
// resumed from the server comes back as an unanswered question. Rebuild the same
// "answer stopped" turn a live stop leaves behind; returns the input unchanged when
// the last turn is a completed answer (nothing was stopped).
function markAnswerStopped(messages: ChatMessage[]): ChatMessage[] {
  const last = messages[messages.length - 1];
  if (!last) return messages;
  if (last.role === "assistant") {
    if (!last.pending) return messages;
    return [
      ...messages.slice(0, -1),
      { ...last, pending: false, canceled: true },
    ];
  }
  return [
    ...messages,
    { ...createMessage("assistant", "", false), canceled: true },
  ];
}

function normalizeBlocks(value: unknown): Block[] | null {
  return Array.isArray(value) ? (value as Block[]) : null;
}

// Restores attachment placeholders for a resumed turn: keeps the metadata
// (fileName/contentType/size) so the message shows a file chip, and preserves base64
// data only if a caller ever supplies it. Unlike normalizeAttachments this does NOT
// require data, since its purpose is display, not re-sending.
function normalizeResumeAttachments(value: unknown): ChatAttachmentInput[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(isRecord)
    .map((attachment) => {
      const data = stringValue(attachment.data);
      return {
        fileName: stringValue(attachment.fileName),
        contentType: stringValue(attachment.contentType),
        size: typeof attachment.size === "number" ? attachment.size : 0,
        ...(data ? { data } : {}),
      };
    })
    .filter((attachment) => !!attachment.fileName)
    .slice(0, MAX_ATTACHMENT_COUNT);
}

// Returns the messages with the given message's feedback set to value, or the key omitted
// when value is null (exactOptionalPropertyTypes forbids feedback: undefined). Used for the
// optimistic apply and the revert-on-failure in submitFeedback.
function withFeedback(
  messages: ChatMessage[],
  messageId: string,
  value: "positive" | "negative" | null,
): ChatMessage[] {
  return messages.map((candidate) => {
    if (candidate.id !== messageId) return candidate;
    if (value === null) {
      const next = { ...candidate };
      delete next.feedback;
      return next;
    }
    return { ...candidate, feedback: value };
  });
}

function normalizeAttachments(
  attachments: ChatAttachmentInput[] | undefined,
): ChatAttachmentInput[] {
  if (!Array.isArray(attachments) || attachments.length === 0) return [];
  return attachments
    .filter(
      (attachment) =>
        !!attachment.fileName &&
        !!attachment.data &&
        attachment.size > 0 &&
        attachment.size <= MAX_ATTACHMENT_BYTES &&
        attachment.data.length <= MAX_ATTACHMENT_BASE64_LENGTH &&
        attachment.contentType.toLowerCase().startsWith("image/"),
    )
    .slice(0, MAX_ATTACHMENT_COUNT)
    .map((attachment) => ({ ...attachment }));
}

function normalizeFeedbackValue(
  feedback: unknown,
  score: unknown,
): Pick<ChatMessage, "feedback"> {
  if (feedback === "positive" || feedback === "negative") return { feedback };
  if (typeof score === "number") {
    if (score >= 50) return { feedback: "positive" };
    if (score > 0) return { feedback: "negative" };
  }
  return {};
}

function normalizeFiltersValue(value: unknown): ChatFilterValue[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).map((filter) => ({
    name: stringValue(filter.name),
    value: stringValue(filter.value),
  }));
}

function trimHistoryText(value: string): string {
  const text = value.replace(/\s+/g, " ").trim();
  if (text.length <= 80) return text;
  return `${text.substring(0, 77)}...`;
}

function normalizeQuestionForMatch(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

function storedQuestionMatchesRequest(
  requestedQuestion: string,
  storedQuestion: string,
): boolean {
  const requested = normalizeQuestionForMatch(requestedQuestion);
  const stored = normalizeQuestionForMatch(storedQuestion);
  const storedHistoryTitle = normalizeQuestionForMatch(
    trimHistoryText(storedQuestion),
  );
  return (
    !!requested &&
    !!stored &&
    (requested === stored || requested === storedHistoryTitle)
  );
}

function getCurrentBaseUrl(): string {
  if (typeof window !== "undefined" && window.location?.href) {
    return window.location.href;
  }
  return "http://localhost/";
}

function stringValue(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (value === null || typeof value === "undefined") return fallback;
  return String(value);
}

function normalizeConversationStatus(
  value: unknown,
): "ready" | "pending" | "error" | "cancelled" | "unknown" {
  if (typeof value !== "string") return "unknown";
  const normalized = value.trim().toLowerCase();
  switch (normalized) {
    case "ready":
    case "pending":
    case "error":
    case "cancelled":
      return normalized;
    default:
      return "unknown";
  }
}

function parseEventData<T extends Record<string, unknown>>(data: string): T {
  try {
    return JSON.parse(data) as T;
  } catch {
    return {} as T;
  }
}

function normalizeSources(values: unknown[] | undefined): SourceReference[] {
  if (!Array.isArray(values)) return [];
  const normalized = values.filter(isRecord).flatMap((value) => {
    const source = normalizeSource(value);
    return source ? [source] : [];
  });
  return normalized;
}

function normalizeSource(
  value: Record<string, unknown>,
): SourceReference | null {
  const kind = normalizeSourceKind(value.kind);
  const rawUrl = typeof value.url === "string" ? value.url : "";
  const url =
    kind === "external"
      ? getSafeExternalUrl(rawUrl)
      : getSafeSourceUrl(rawUrl, getCurrentBaseUrl());
  if (!url) return null;
  return {
    title: toSourceText(value.title),
    url,
    label: toSourceText(value.label),
    excerpt: toSourceText(value.excerpt),
    sourcetag: toSourceText(value.sourcetag),
    kind,
    opensInNewTab: value.opensInNewTab === true || kind === "external",
  };
}

function normalizeSourceKind(value: unknown): SourceReference["kind"] {
  return value === "video" || value === "external" ? value : "help-article";
}

function toSourceText(value: unknown): string {
  if (value === null || typeof value === "undefined") return "";
  return String(value);
}

function normalizeFollowUps(values: unknown[] | undefined): FollowUp[] {
  if (!Array.isArray(values)) return [];
  const normalized = values.filter(isRecord).flatMap((value) => {
    const question =
      typeof value.question === "string"
        ? value.question
        : typeof value.text === "string"
          ? value.text
          : "";
    const label = typeof value.label === "string" ? value.label : question;
    if (!question && !label) return [];
    return [
      {
        label,
        question: question || label,
        intent: typeof value.intent === "string" ? value.intent : "",
      },
    ];
  });
  return normalized;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isLocalFallbackToken(token: string): boolean {
  return token.startsWith("local-fallback-");
}
